import { datePl, type DemandDto, type DemandLineDto, NEEDED_BY_SOURCES } from '@bqcrm/shared';
import { ActionIcon, Anchor, Badge, Button, Group, Menu, Modal, NumberInput, Stack, Table, Text, Textarea, Tooltip } from '@mantine/core';
import { IconDots } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../api';
import { DemandSourceBadge, ReadinessBadge, TradeBadge } from '../../components/Badges';
import { qtyPl } from '../../format';
import { notifyError, notifyOk } from '../../notify';
import { ItemSelect, useStockItems } from './common';

/** Odświeżenie wszystkich widoków zapotrzebowań, stanów i materiałów sprawy. */
export function useDemandRefresh() {
  const qc = useQueryClient();
  return () => {
    for (const k of ['demands', 'demand', 'case-materials', 'stock', 'stock-item', 'reservations', 'shortages', 'packages', 'package']) void qc.invalidateQueries({ queryKey: [k] });
  };
}

/** Nagłówek zapotrzebowania: źródło, sprawa, zadanie, termin gotowości (ze źródłem), gotowość. */
export function DemandHeader({ d, linkCase = true }: { d: DemandDto; linkCase?: boolean }) {
  return (
    <Group gap="xs" wrap="wrap">
      <DemandSourceBadge source={d.source} />
      {d.tradeCode && <TradeBadge code={d.tradeCode} />}
      {d.caseNumber &&
        (linkCase && d.caseId ? (
          <Anchor component={Link} to={`/sprawy/${d.caseId}?zakladka=materialy`} size="sm" fw={600}>
            {d.caseNumber}
          </Anchor>
        ) : (
          <Text size="sm" fw={600}>
            {d.caseNumber}
          </Text>
        ))}
      {d.contractNumber && (
        <Text size="xs" c="dimmed">
          umowa {d.contractNumber}
        </Text>
      )}
      {d.taskNumber && (
        <Text size="xs" c="dimmed">
          zadanie {d.taskNumber}
          {d.teamName ? ` · ${d.teamName}` : ''}
        </Text>
      )}
      <Tooltip label={d.neededBySource ? `Termin gotowości ${NEEDED_BY_SOURCES[d.neededBySource]}` : 'Bez terminu — najniższy priorytet w kolejce rezerwacji'}>
        <Badge size="sm" variant="outline" color={d.neededBy ? 'gray' : 'yellow'}>
          {d.neededBy ? `gotowe do ${datePl(d.neededBy)}` : 'bez terminu'}
        </Badge>
      </Tooltip>
      <ReadinessBadge readiness={d.readiness} />
      {d.unreviewed && (
        <Badge size="sm" color="cyan" variant="filled">
          {d.changedAt && new Date(d.changedAt).getTime() - new Date(d.createdAt).getTime() > 5000 ? 'Zmienione' : 'Nowe'}
        </Badge>
      )}
      {d.status !== 'otwarte' && (
        <Badge size="sm" color="gray" variant="light">
          {d.status === 'zrealizowane' ? 'Zrealizowane' : 'Anulowane'}
        </Badge>
      )}
    </Group>
  );
}

/**
 * Pozycje zapotrzebowania: potrzebne, zarezerwowane, wydane, brak. Działania (magazynier): zmiana ilości,
 * dopasowanie pozycji opisowej, ręczna rezerwacja braku.
 */
export function DemandLinesTable({ d, manage, warehouseId }: { d: DemandDto; manage: boolean; warehouseId: string }) {
  const [change, setChange] = useState<DemandLineDto | null>(null);
  const [match, setMatch] = useState<DemandLineDto | null>(null);
  const [reserveFor, setReserveFor] = useState<DemandLineDto | null>(null);
  return (
    <>
      <Table.ScrollContainer minWidth={620}>
        <Table verticalSpacing={4}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Produkt</Table.Th>
              <Table.Th ta="right">Potrzeba</Table.Th>
              <Table.Th ta="right">Zarezerw.</Table.Th>
              <Table.Th ta="right">Wydano</Table.Th>
              <Table.Th ta="right">Brak</Table.Th>
              {manage && <Table.Th w={36} />}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {d.lines.map((l) => (
              <Table.Tr key={l.id} style={{ opacity: l.quantity === 0 && l.issued === 0 ? 0.5 : 1 }}>
                <Table.Td>
                  <Text size="sm">{l.name}</Text>
                  <Group gap={4}>
                    {l.code && (
                      <Text size="xs" c="dimmed">
                        {l.code}
                      </Text>
                    )}
                    {!l.itemId && (
                      <Badge size="xs" color="yellow" variant="light">
                        opis — do dopasowania
                      </Badge>
                    )}
                    {l.additional && (
                      <Badge size="xs" color="cyan" variant="light">
                        dodatkowo
                      </Badge>
                    )}
                    {l.pendingQuantity !== null && (
                      <Badge size="xs" color="red" variant="filled">
                        zmiana od ekipy → {qtyPl(l.pendingQuantity)} (do decyzji w pakiecie)
                      </Badge>
                    )}
                    {l.cancelled > 0 && (
                      <Text size="xs" c="dimmed">
                        anulowano {qtyPl(l.cancelled)}
                      </Text>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td ta="right" className="tabular">
                  {qtyPl(l.quantity)} {l.unit}
                </Table.Td>
                <Table.Td ta="right" className="tabular">
                  {l.reserved ? qtyPl(l.reserved) : '—'}
                </Table.Td>
                <Table.Td ta="right" className="tabular">
                  {l.issued ? qtyPl(l.issued) : '—'}
                </Table.Td>
                <Table.Td ta="right" className="tabular">
                  <Text size="sm" fw={l.shortage ? 700 : 400} c={l.shortage ? 'orange' : 'dimmed'}>
                    {l.shortage ? qtyPl(l.shortage) : '—'}
                  </Text>
                  {l.ordered > 0 && (
                    <Text size="xs" c={l.orderedUnplaced ? 'dimmed' : 'blue'}>
                      zamówione {qtyPl(l.ordered)}
                      {l.orderedUnplaced ? ' (niezłożone)' : l.orderedBy ? ` na ${datePl(l.orderedBy)}` : ''}
                    </Text>
                  )}
                </Table.Td>
                {manage && (
                  <Table.Td>
                    <Menu position="bottom-end">
                      <Menu.Target>
                        <ActionIcon variant="subtle" color="gray" aria-label="Działania">
                          <IconDots size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        {!l.itemId && <Menu.Item onClick={() => setMatch(l)}>Dopasuj do produktu…</Menu.Item>}
                        {l.itemId && l.shortage > 0 && <Menu.Item onClick={() => setReserveFor(l)}>Zarezerwuj ręcznie…</Menu.Item>}
                        <Menu.Item onClick={() => setChange(l)}>Zmień potrzebną ilość…</Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {change && <LineChangeModal line={change} onClose={() => setChange(null)} />}
      {match && <MatchModal line={match} onClose={() => setMatch(null)} />}
      {reserveFor && <ReserveModal line={reserveFor} warehouseId={warehouseId} onClose={() => setReserveFor(null)} />}
    </>
  );
}

function LineChangeModal({ line, onClose }: { line: DemandLineDto; onClose: () => void }) {
  const [qty, setQty] = useState<number>(line.quantity);
  const [reason, setReason] = useState('');
  const refresh = useDemandRefresh();
  const save = useMutation({
    mutationFn: () => api(`/demand-lines/${line.id}`, { method: 'PUT', body: { quantity: qty, reason } }),
    onSuccess: () => {
      notifyOk('Zmieniono potrzebę');
      refresh();
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title="Zmiana potrzebnej ilości">
      <Stack>
        <Text size="sm">
          {line.name}: teraz {qtyPl(line.quantity)} {line.unit}, wydano {qtyPl(line.issued)}. Zmniejszenie zwalnia nadmiar rezerwacji; wydanych ilości nie zmienia.
        </Text>
        <NumberInput label={`Potrzeba (${line.unit})`} min={line.issued} decimalScale={3} decimalSeparator="," value={qty} onChange={(v) => setQty(Number(v) || 0)} />
        <Textarea label="Przyczyna" required autosize minRows={2} value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={reason.trim().length < 3 || qty === line.quantity}>
            Zapisz
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function MatchModal({ line, onClose }: { line: DemandLineDto; onClose: () => void }) {
  const items = useStockItems();
  const [itemId, setItemId] = useState<string | null>(null);
  const [qty, setQty] = useState<number>(line.quantity);
  const refresh = useDemandRefresh();
  const it = items.data?.find((i) => i.id === itemId);
  const save = useMutation({
    mutationFn: () => api(`/demand-lines/${line.id}/match`, { method: 'POST', body: { itemId, quantity: qty } }),
    onSuccess: () => {
      notifyOk('Dopasowano pozycję');
      refresh();
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title="Dopasowanie pozycji do produktu">
      <Stack>
        <Text size="sm">
          Ekipa opisała: <b>{line.description}</b> — {qtyPl(line.quantity)} {line.unit}
        </Text>
        <ItemSelect label="Produkt z katalogu" items={items.data ?? []} value={itemId} onChange={setItemId} />
        <NumberInput label={`Ilość${it ? ` (${it.unit})` : ''}`} description="Przelicz, jeśli ekipa podała inną jednostkę." min={0.001} decimalScale={3} decimalSeparator="," value={qty} onChange={(v) => setQty(Number(v) || 0)} />
        <Text size="xs" c="dimmed">
          Brak produktu w katalogu? Dodaje go osoba z uprawnieniem do edycji katalogu (Sprzedaż → Katalog).
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!itemId || qty <= 0}>
            Dopasuj
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function ReserveModal({ line, warehouseId, onClose }: { line: DemandLineDto; warehouseId: string; onClose: () => void }) {
  const [qty, setQty] = useState<number>(line.shortage);
  const refresh = useDemandRefresh();
  const save = useMutation({
    mutationFn: () => api('/reservations', { method: 'POST', body: { warehouseId, itemId: line.itemId, quantity: qty, demandLineId: line.id } }),
    onSuccess: () => {
      notifyOk('Zarezerwowano');
      refresh();
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title="Ręczna rezerwacja">
      <Stack>
        <Text size="sm">
          {line.name}: brak {qtyPl(line.shortage)} {line.unit}. Rezerwacja tylko z towaru dostępnego (niezarezerwowanego) — nie odbiera innym realizacjom. Żeby zabrać towar innej realizacji, użyj przeniesienia rezerwacji w stanach produktu.
        </Text>
        <NumberInput label={`Ilość (${line.unit})`} min={0.001} max={line.shortage} decimalScale={3} decimalSeparator="," value={qty} onChange={(v) => setQty(Number(v) || 0)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={qty <= 0}>
            Zarezerwuj
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

